import { classNames } from 'primereact/utils';
import { formatDateString, isLlmErrorMarker } from '@repo/utils';
import { SectionsTreeNode } from '../types';

interface SectionsTreeNodeContentProps {
  data: SectionsTreeNode;
}

// Placeholder cluster name for a multi-date document; shown as the headline.
const LONG_MULTI_DATE_LABEL = 'Long Multi-Date Document';

const pageCountText = (count: number) => `${count}${count === 1 ? ' page' : ' pages'}`;

export const SectionsTreeNodeContent: React.FC<SectionsTreeNodeContentProps> = ({ data }) => {
  if (data.type === 'cluster') {
    const nameIsLongMultiDate = data.name === LONG_MULTI_DATE_LABEL;
    const shortTitle = data.shortTitle && isLlmErrorMarker(data.shortTitle) ? undefined : data.shortTitle;
    const topLine = nameIsLongMultiDate ? data.name : shortTitle || 'Sub-document';
    // Bottom row's document name — omitted when the name has already been
    // promoted to the headline above.
    const bottomName = nameIsLongMultiDate ? null : data.name;
    const dateSuffix = data.date ? ` (${formatDateString(data.date)})` : '';
    return (
      <div className="flex min-w-0 flex-1 flex-col justify-center">
        <span className="text-fletchers-black overflow-hidden text-[0.875rem] font-semibold overflow-ellipsis whitespace-nowrap">
          {topLine}
        </span>
        <div className="flex min-w-0 items-center gap-2">
          {bottomName && (
            <span className="text-fletchers-black min-w-0 overflow-hidden text-[0.8125rem] font-normal overflow-ellipsis whitespace-nowrap">
              {bottomName}
              {dateSuffix}
            </span>
          )}
          {!bottomName && data.date && (
            <span className="text-fletchers-gray-200 shrink-0 text-[0.8125rem] font-normal">
              ({formatDateString(data.date)})
            </span>
          )}
          {!!data.numberOfPages && (
            <span className="text-fletchers-green-500 shrink-0 text-[0.8125rem] font-normal">
              {pageCountText(data.numberOfPages)}
            </span>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-w-0 flex-1 flex-col justify-center">
      {!!data.name && (
        <span
          className={classNames(
            'text-fletchers-black text-[0.875rem] font-semibold',
            'overflow-hidden overflow-ellipsis whitespace-nowrap',
          )}
        >
          {data.name}
        </span>
      )}
      <div className="flex items-center gap-2">
        {data.type === 'provider' && !!data.date && (
          <span className="bg-fletchers-green-50 text-fletchers-green-500 border-fletchers-green-100 w-fit rounded border px-[6px] py-[1px] text-[0.6875rem] font-medium">
            Added {formatDateString(data.date)}
          </span>
        )}
        {!!data.numberOfPages && (
          <span
            className={classNames(
              'text-fletchers-green-500 text-[0.8125rem] font-normal',
              'overflow-hidden overflow-ellipsis whitespace-nowrap',
            )}
          >
            {pageCountText(data.numberOfPages)}
          </span>
        )}
      </div>
    </div>
  );
};
